import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Check } from "lucide-react";

interface CelebrationOverlayProps {
  type: "confetti" | "checkmark";
  onComplete: () => void;
}

// Simple confetti particle component
function ConfettiParticle({ delay, x, color }: { delay: number; x: number; color: string }) {
  return (
    <motion.div
      className="absolute top-0 rounded-sm"
      style={{
        left: `${x}%`,
        width: 8,
        height: 8,
        backgroundColor: color,
      }}
      initial={{ y: -20, opacity: 1, rotate: 0, scale: 1 }}
      animate={{
        y: window.innerHeight + 20,
        opacity: [1, 1, 0],
        rotate: [0, 360, 720],
        scale: [1, 1.2, 0.8],
        x: [0, (Math.random() - 0.5) * 100],
      }}
      transition={{
        duration: 2 + Math.random(),
        delay,
        ease: "easeIn",
      }}
    />
  );
}

const COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899"];

export function CelebrationOverlay({ type, onComplete }: CelebrationOverlayProps) {
  const [particles] = useState(() =>
    Array.from({ length: 40 }, (_, i) => ({
      id: i,
      delay: Math.random() * 0.3,
      x: Math.random() * 100,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
    }))
  );

  useEffect(() => {
    const timer = setTimeout(onComplete, type === "confetti" ? 2500 : 1500);
    return () => clearTimeout(timer);
  }, [onComplete, type]);

  if (type === "checkmark") {
    return (
      <div className="fixed inset-0 z-[60] pointer-events-none flex items-center justify-center">
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0, opacity: 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 20 }}
          className="flex h-20 w-20 items-center justify-center rounded-full bg-green-500 shadow-lg"
        >
          <motion.div
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ delay: 0.2, duration: 0.4 }}
          >
            <Check className="h-10 w-10 text-white" strokeWidth={3} />
          </motion.div>
        </motion.div>
      </div>
    );
  }

  // Confetti
  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[60] pointer-events-none overflow-hidden">
        {particles.map((p) => (
          <ConfettiParticle key={p.id} delay={p.delay} x={p.x} color={p.color} />
        ))}
        {/* Central message */}
        <motion.div
          className="absolute inset-0 flex items-center justify-center"
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
        >
          <div className="rounded-2xl bg-white/90 px-8 py-5 shadow-xl backdrop-blur dark:bg-slate-900/90">
            <p className="text-center text-xl font-bold text-slate-900 dark:text-slate-100">
              🎉
            </p>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
